import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EnhancedActivityForm } from "@/components/activity/EnhancedActivityForm";
import { fetchActivityById, updateActivity } from "@/services/activity/activityService";

export default function EditActivity() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activity, setActivity] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadActivity = async () => {
      if (!id) return;
      
      try {
        setIsLoading(true);
        const data = await fetchActivityById(id);
        setActivity(data);
      } catch (err: any) {
        setError(err.message || "Failed to load activity");
        toast({
          title: "Error",
          description: "Failed to load activity details",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadActivity();
  }, [id, toast]);

  const handleSubmit = async (values: any) => {
    if (!id) return;
    
    try {
      setIsSubmitting(true);
      await updateActivity(id, values);
      
      toast({
        title: "Success",
        description: "Activity updated successfully",
      });
      
      navigate("/activities");
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to update activity",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/activities">
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Back to Activities</span>
            </Link>
          </Button>
          <h1 className="text-2xl font-bold">Edit Activity</h1>
        </div>
        <Card>
          <CardContent className="p-6 flex justify-center items-center min-h-[200px]">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !activity) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/activities">
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Back to Activities</span>
            </Link>
          </Button>
          <h1 className="text-2xl font-bold">Edit Activity</h1>
        </div>
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-destructive">{error || "Failed to load activity"}</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => navigate("/activities")}
            >
              Back to Activities
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/activities">
            <ArrowLeft className="h-4 w-4" />
            <span className="sr-only">Back to Activities</span>
          </Link>
        </Button>
        <h1 className="text-2xl font-bold">Edit Activity</h1>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>{activity.title}</CardTitle>
        </CardHeader>
        <CardContent>
          <EnhancedActivityForm 
            initialValues={activity}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            submitLabel="Update Activity"
          />
        </CardContent>
      </Card>
    </div>
  );
}